import { useEffect, useMemo, useRef, useState } from "react";
import { RewardsService } from "../../../../services/RewardsService";
import { ChangeAlertCallbackType } from "../../../Root";
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { Button, Switch } from "@mui/material";
import { useItemConfirm } from "../../../modals/confirm/hooks/useItemConfirm";
import { ModalStyle } from "../../../../util/Styles";
import { generalDate } from "../../../elements/DateRangePicker";
import { rewardsLevelType } from "../../../../util/DataObject";
import moment from "moment";
moment.locale("es");

export const useRewardsTable = (changeAlert: ChangeAlertCallbackType, dialogProps, dialogFilter) => {
    const rewardsService = useRef<RewardsService>();
    const [data, setData] = useState([]);
    const [row, setRow] = useState({});
    const [action, setAction] = useState("");
    const [loading, setLoading] = useState(false);
    const [title, setTitle] = useState("");
    const [catalog, setCatalog] = useState([]);
    const [tabActive, setTabActive] = useState('promotions');
    const [tabs, setTabs] = useState([]);

    const { DialogConfirm, confirm: confirmRemove } = useItemConfirm(
        `Eliminar ${tabs.length > 0 && tabs.filter(item => item.name === tabActive)[0]["title"]}`,
        '¿Estás seguro que deseas eliminar el siguiente registro?'
    )

    const onSetTabs = (config) => {
        const options = [
            { name: 'promotions', title: 'Promoción' },
            { name: 'configuration', title: 'Configuración' },
            { name: 'history_promo', title: 'Hitorial - Promos' },
            { name: 'history_config', title: 'Historial - Config' }
        ];
        let t = options.filter((action) => config && config["actions"]?.indexOf(action.name) > -1)
        setTabs(t);
        return t.length > 0 ? true : false
    }

    const getData = async (service = "rewardsPromotionsGet", tab= tabActive) => {
        let s = {
            data: {
                status: tab.includes("history") ? "disabled" : "enabled",
                active: tab.includes("history") ? false : true
            }
        }

        setLoading(true);
        const response = await rewardsService.current[service](s);
        if (response.data?.response === "OK") {
            let cf = response.data.widget[0]['config']
            let validate = onSetTabs(cf);

            setLoading(false);
            validate && setData(response.data?.content['coupons'] || []);
        } else {
            setLoading(false);
            changeAlert(true, "error", response.data.message);
        }
        setLoading(false);
    };

    const filterReward = async (search) => {
        dialogFilter.onClose()

        setLoading(true);
        let service = tabActive === "promotions" ? "rewardsPromotionsGet" : "rewardConfigurationGet"
        const response = await rewardsService.current[service]({data: search});

        if (response.data?.response === "OK") {
            setLoading(false);
            setData(response.data?.content['coupons'] || []);
        } else {
            setLoading(false);
            changeAlert(true, "error", response.data.message);
        }
        setLoading(false);
    }

    const buildPayload = (item, action) => {
        let service = "";
        let payload = {};
        let getService = "";
        let active  = action === "update" ? true : false
        let status = action === "update" ? item["status"] === "enabled" ? "disabled" : "enabled" : "disabled"

        switch (tabActive) {
            case "promotions":
            case "history_promo":
                payload = {
                    uuid: item.uuid,
                    title: item.title,
                    description: item.description,
                    active: active,
                    status: status,
                    multiplier: item.metadata.multiplier
                }
                service = "rewardsPromotionsUpdate"
                getService = "rewardsPromotionsGet"
                break;
            case "configuration":
            case "history_config":
                payload = {
                    uuid: item.uuid,
                    active: active,
                    status: status
                }
                service = "rewardConfigurationUpdate" 
                getService = "rewardConfigurationGet"
                break;
        }
        return {service, payload, getService}
    }

    const onDeleteReward = async (item) => {
        const isConfirm = await confirmRemove(item["title"] || item["level_uuid"] )
        if (isConfirm) {
            setLoading(true);
            const { service, payload, getService } = buildPayload(item, "delete")
            const response = await rewardsService.current[service]({data: payload});

            if (response.data?.response === "OK") {
                setLoading(false);
                changeAlert(true, "success", "El registro ha sido eliminado con éxito");
                getData(getService);
            } else {
                setLoading(false);
                changeAlert(true, "error", response.data.message);
            }
        }
    }

    const updateStatus = async (item) => {
        setLoading(true);
        const { service, payload, getService } = buildPayload(item, "update")
        const response = await rewardsService.current[service]({data: payload});

        if (response.data?.response === "OK") {
            setLoading(false);
            changeAlert(true, "success", "El registro ha sido actualizado con éxito");
            getData(getService);
        } else {
            setLoading(false);
            changeAlert(true, "error", response.data.message);
        }
    }

    const changeTab = (tab: string) => {
        setTabActive(tab);
        setData([]);
        
        tab === "promotions" || tab === "history_promo" ? getData("rewardsPromotionsGet", tab) : getData("rewardConfigurationGet", tab)
    };

    const onOpenDialogReward = (action, row = null) => {
        setRow(row);
        setAction(action);

        (tabActive ===  "promotions" || tabActive === "history_promo") ? setTitle(`${action} Promoción`) : setTitle(`${action} Configuración`)
        dialogProps.openDialog();
    };
    const onCloseDialogReward = () => {
        dialogProps.onClose();
    };

    const validateCatalog = (item, cat) => {
        let val = cat.filter(c => c.m[0]["acumulator_description"] === item)
        return val.length === 1 ? val[0]["v"] : "N/A"
    }

    useEffect(() => {
        rewardsService.current = new RewardsService(changeAlert);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const calculateColumns = useMemo(() => {
        const columns = [];

        tabActive === "promotions" || tabActive === "history_promo" ?
        columns.push(
            { selector: (row) => row.title, name: "Titulo", width: "20%", wrap: true, sortable: true },
            { selector: (row) => catalog.length > 0 && validateCatalog(row.description, catalog), name: "Tipo de Operación", width: "20%", wrap: true, sortable: true },
            { selector: (row) => row.metadata.multiplier, name: "Multiplicador", wrap: true, sortable: true, center: true },
            { selector: (row) => generalDate(row.metadata.period.sdate), name: "Periodo - Inicio", wrap: true, sortable: true },
            { selector: (row) => generalDate(row.metadata.period.edate), name: "Periodo - Fin", wrap: true, sortable: true },
            { cell: (row) => {
                return <Switch checked={row.status === "enabled" ? true : false } onChange={
                    row.status === "disabled" && !row.active ? onOpenDialogReward.bind(this, "Editar", row) : updateStatus.bind(this, row)
                } color='secondary'/>
                }, name: 'Estatus', width:'10%', wrap: true, sortable: true, center: true
            },
            { cell: (row) => {
                    return (
                        <ModalStyle property={String(!row.active)}>
                            <Button onClick={onOpenDialogReward.bind(this, "Editar", row)} disabled={!row.active} sx={{minWidth: "40px", p: 0.7, mr: 2}} variant="contained" color="secondary">
                                <EditIcon />
                            </Button>
                            <Button onClick={onDeleteReward.bind(this, row)} disabled={!row.active} sx={{minWidth: "40px", p: 0.7}} variant="contained" color="secondary">
                                <DeleteIcon />
                            </Button>
                        </ModalStyle>
                    );
                },
                name: "Acciones", wrap: true, sortable: true, center: true
            }
        )
        :
        columns.push(
            { selector: (row) => rewardsLevelType.filter(l => l.key === row.level_uuid)[0]["label"], name: "Nivel", wrap: true, sortable: true },
            { selector: (row) => row.priority, name: "Prioridad", wrap: true, sortable: true },
            { selector: (row) => generalDate(row.period.sdate), name: "Periodo - Inicio", wrap: true, sortable: true },
            { selector: (row) => generalDate(row.period.edate), name: "Periodo - Fin", wrap: true, sortable: true },
            { cell: (row) => {
                return <Switch checked={row.status === "enabled" ? true : false } onChange={
                    row.status === "disabled" && !row.active ? onOpenDialogReward.bind(this, "Editar", row) : updateStatus.bind(this, row)
                } color='secondary'/>
                }, name: 'Estatus', width:'10%', wrap: true, sortable: true, center: true
            },
            { cell: (row) => {
                    return (
                        <ModalStyle property={String(!row.active)}>
                            <Button onClick={onOpenDialogReward.bind(this, "Editar", row)} disabled={!row.active} sx={{minWidth: "40px", p: 0.7, mr: 2}} variant="contained" color="secondary">
                                <EditIcon />
                            </Button>
                            <Button onClick={onDeleteReward.bind(this, row)} disabled={!row.active} sx={{minWidth: "40px", p: 0.7}} variant="contained" color="secondary">
                                <DeleteIcon />
                            </Button>
                        </ModalStyle>
                    );
                },
                name: "Acciones", wrap: true, sortable: true, center: true
            },
        )

        return columns;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tabActive, catalog]);

    return {
        columns: calculateColumns,
        data,
        loading,
        tabActive,
        tabs,
        title,
        row,
        action,
        getData,
        onSetTabs,
        setCatalog,
        changeTab,
        onOpenDialogReward,
        onCloseDialogReward,
        DialogConfirm,
        filterReward
    };
};
